import React, { useEffect, useLayoutEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from '@/contexts/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { GoogleCallback } from '@/components/GoogleCallback';
import { LandingPage } from '@/pages/LandingPage';
import { LoginPage } from '@/pages/LoginPage';
import { RegisterPage } from '@/pages/RegisterPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import { ServicesPage } from '@/pages/ServicesPage';
import { ServiceDetailPage } from '@/pages/ServiceDetailPage';
import { ScooterMarketplace } from '@/pages/ScooterMarketplace';
import { ScooterDetail } from '@/pages/ScooterDetail';
import { TransportMarketplace } from '@/pages/TransportMarketplace';
import { ProviderListings } from '@/pages/ProviderListings';
import { DashboardRouter } from '@/pages/DashboardRouter';
import AboutPage from '@/pages/AboutPage';
import FAQPage from '@/pages/FAQPage';
import ContactPage from '@/pages/ContactPage';
import LegalPage from '@/pages/LegalPage';
import TermsPage from '@/pages/TermsPage';
import PrivacyPage from '@/pages/PrivacyPage';
import NotFoundPage from '@/pages/NotFoundPage';
import AccountNotificationsPage from '@/pages/AccountNotificationsPage';
import LocalScooterPatong from '@/pages/LocalScooterPatong';
import AirportTransferPhuket from '@/pages/AirportTransferPhuket';
import { Toaster } from '@/components/ui/sonner';
import { InstallPrompt } from '@/components/InstallPrompt';
import { PushRuntime } from '@/components/PushRuntime';
import { SiteMetricsRuntime } from '@/components/SiteMetricsRuntime';
import { updateHtmlLang } from '@/i18n/updateHtmlLang';
import { routeLanguage, routerBasename } from '@/i18n/urlLanguage';
import '@/i18n'; // Initialise i18next au boot de l'app
import '@/App.css';

function AppRouter() {
  const location = useLocation();
  const { i18n } = useTranslation();

  // One language per public URL: /en/… English, other public pages French
  // (private pages keep the session's language). Before paint: no flash.
  useLayoutEffect(() => {
    const lang = routeLanguage(`${BASENAME}${location.pathname}`, location.hash);
    if (lang && !(i18n.resolvedLanguage || i18n.language || '').startsWith(lang)) i18n.changeLanguage(lang);
  }, [location.pathname, location.hash, i18n]);

  // Synchronise l'attribut <html lang> avec la langue active i18next
  useEffect(() => {
    updateHtmlLang(i18n.language);
  }, [i18n.language]);
  
  // Check URL fragment for session_id synchronously during render (NOT in useEffect)
  if (location.hash?.includes('session_id=')) {
    return <GoogleCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/services/:serviceId" element={<ServiceDetailPage />} />
      <Route path="/scooters" element={<ScooterMarketplace />} />
      <Route path="/scooters/:id" element={<ScooterDetail />} />
      <Route path="/provider/:providerId" element={<ProviderListings />} />
      <Route path="/transport" element={<TransportMarketplace />} />
      {/* Local SEO pages: French slug, English slug under /en (urlLanguage.pathInLang). */}
      <Route path="/location-scooter-patong" element={<LocalScooterPatong />} />
      <Route path="/scooter-rental-patong" element={<LocalScooterPatong />} />
      <Route path="/transfert-aeroport-phuket" element={<AirportTransferPhuket />} />
      <Route path="/phuket-airport-transfer" element={<AirportTransferPhuket />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/mentions-legales" element={<LegalPage />} />
      <Route path="/conditions-generales" element={<TermsPage />} />
      <Route path="/politique-confidentialite" element={<PrivacyPage />} />
      <Route path="/google-callback" element={<GoogleCallback />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardRouter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/notifications"
        element={
          <ProtectedRoute>
            <AccountNotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

// "/en" on English URLs (/en/…): every Link / navigate() keeps the prefix.
const BASENAME = routerBasename(window.location.pathname);

function App() {
  return (
    <BrowserRouter basename={BASENAME}>
      <AuthProvider>
        <AppRouter />
        <InstallPrompt />
        <PushRuntime />
        <SiteMetricsRuntime />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;